/**
 * TutorialTree — grouped tutorial list for the Video Tutorials tab.
 *
 * Props:
 *   highlightId  {string}   — tutorial id to auto-scroll and highlight (e.g. "06")
 *   onPlay       {fn}       — called with the tutorial object when Watch is clicked
 */

import React, { useRef, useEffect } from "react";

/* ── Tutorial data ─────────────────────────────────────────────────────── */
export const TUTORIALS = [
  {
    series: "Onboarding",
    color: "#545cd8",
    icon: "🚀",
    items: [
      {
        id: "00", title: "Welcome to Growfin", duration: "30s", priority: "P0",
        desc: "30-second overview of what Growfin does, the strategies it supports, and why it matters.",
        routes: ["/dashboard"],
        topics: ["What Growfin is", "Strategies supported", "Platform overview"],
        videoSrc: "/apps/growfin-tutorials/out/00-intro.mp4",
      },
      {
        id: "01", title: "Dashboard Walkthrough", duration: "90s", priority: "P0",
        desc: "Navigate the main dashboard: portfolio P&L, active strategies count, Greek summary, and rule fire activity.",
        routes: ["/dashboard"],
        topics: ["Portfolio P&L card", "Active strategies badge", "Option Greeks at a glance", "Rule fire activity log", "Navigation map"],
      },
    ],
  },
  {
    series: "Strategy",
    color: "#3ac47d",
    icon: "📈",
    items: [
      {
        id: "02", title: "Building Your First Strategy", duration: "3m", priority: "P0",
        desc: "Create and activate a Delta-Hedged Short Strangle or Pyramid Hedging strategy from the Strategy Builder.",
        routes: ["/strategy/build", "/strategy/manage"],
        topics: ["Select symbol & LTP", "Choose strategy type", "Configure key settings", "Read the payoff chart", "Activate your strategy"],
      },
      {
        id: "10", title: "DHSS Deep Dive", duration: "4m", priority: "P1",
        desc: "Master Delta-Hedged Short Strangle mechanics: delta neutrality, threshold modes, and expiry management rules.",
        routes: ["/strategy/build"],
        topics: ["Short strangle concept", "Delta band filtering", "Static vs adaptive vs expression threshold", "SQUARE_OFF_MAX_LOTS logic", "Expiry management"],
      },
      {
        id: "11", title: "Pyramid Hedging Deep Dive", duration: "4m", priority: "P2",
        desc: "Understand the pyramid accumulation and call hedge mechanics: ladder construction, roll-down, and derived state.",
        routes: ["/strategy/build"],
        topics: ["Stock ladder concept", "Call hedge selection", "Roll-down trigger", "Reading strategy JSON derived state"],
      },
      {
        id: "12", title: "Reading Payoff Charts", duration: "90s", priority: "P2",
        desc: "Interpret the strategy payoff diagram: breakeven lines, profit zones, and how the chart updates live.",
        routes: ["/strategy/build"],
        topics: ["X/Y axis meaning", "Call & put breakeven lines", "Max profit zone", "Live chart updates"],
      },
    ],
  },
  {
    series: "Rules",
    color: "#f7b924",
    icon: "⚙️",
    items: [
      {
        id: "03", title: "Understanding the Rule Engine", duration: "3m", priority: "P0",
        desc: "The conceptual foundation: how facts, conditions, and actions work together to drive automated decisions.",
        routes: ["/rules/builder"],
        topics: ["Facts vs conditions vs actions", "Fact registry overview", "Complete rule walkthrough", "Groups, priority & cooldown", "group_max_fires"],
      },
      {
        id: "04", title: "Creating Rules from Templates", duration: "2m", priority: "P1",
        desc: "Import and activate pre-built rule templates for your strategy in under 2 minutes.",
        routes: ["/rules/templates"],
        topics: ["Default DHSS template (11 rules)", "Rule groups explained", "Activating a template", "Priority order"],
      },
      {
        id: "05", title: "Building Custom Rules", duration: "2.5m", priority: "P1",
        desc: "Build advanced IF/THEN rules from scratch using the visual editor with conditions, groups, and cooldowns.",
        routes: ["/rules/builder"],
        topics: ["Adding conditions", "AND/OR logic", "Setting actions", "Group & priority config", "Saving and validating"],
      },
    ],
  },
  {
    series: "Monitoring & Execution",
    color: "#30b1ff",
    icon: "👁️",
    items: [
      {
        id: "06", title: "Monitoring Positions & Greeks", duration: "2m", priority: "P1",
        desc: "Read your live option positions, understand delta/theta/IV columns, and track P&L in real-time.",
        routes: ["/trading/positions", "/trading/strategy-summary", "/trading/orders", "/alerts/price"],
        topics: ["Option positions table", "Delta, theta, IV explained", "ITM indicator", "Unrealised vs potential P&L", "Order history execution trail"],
      },
      {
        id: "07", title: "The Automation Approval Flow", duration: "2m", priority: "P0",
        desc: "How rule-generated suggestions become executed trades: approve, reject, snooze, and the full audit trail.",
        routes: ["/automation/suggestions", "/automation/orders", "/automation/history"],
        topics: ["Rule fire → suggestion flow", "Approve a suggestion", "Snooze vs reject", "Order queue status", "Execution history"],
      },
      {
        id: "08", title: "Setting Up Price Alerts", duration: "90s", priority: "P2",
        desc: "Configure Telegram and email price threshold alerts on any tracked symbol.",
        routes: ["/alerts/price", "/trading/positions"],
        topics: ["Add a price alert", "Above/below threshold", "Telegram & email channels", "Alert firing"],
      },
    ],
  },
  {
    series: "Settings",
    color: "#d92550",
    icon: "🔧",
    items: [
      {
        id: "09", title: "Configuring Your Account", duration: "90s", priority: "P1",
        desc: "Set global delta thresholds, connect your broker, configure notifications, and review your plan limits.",
        routes: ["/system/settings"],
        topics: ["Delta thresholds", "Adjustment type", "Broker account setup", "Telegram/email notifications", "Subscription plan"],
      },
    ],
  },
];

/* lookup: tutorialId → tutorial object */
export function getTutorialById(id) {
  for (const group of TUTORIALS) {
    const found = group.items.find(t => t.id === id);
    if (found) return { ...found, series: group.series };
  }
  return null;
}

/* lookup: route path → best tutorial id */
export function getTutorialForRoute(path) {
  for (const group of TUTORIALS) {
    for (const item of group.items) {
      if (item.routes?.includes(path)) return item.id;
    }
  }
  return null;
}

/* ── Component ──────────────────────────────────────────────────────────── */
export default function TutorialTree({ highlightId, onPlay }) {
  const highlightRef = useRef(null);

  /* auto-scroll to highlighted tutorial */
  useEffect(() => {
    if (highlightRef.current) {
      setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
    }
  }, [highlightId]);

  return (
    <div style={{ padding: "24px 28px", maxWidth: 860, margin: "0 auto" }}>
      {TUTORIALS.map(group => (
        <div key={group.series} style={{ marginBottom: 36 }}>
          {/* series header */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            marginBottom: 14, paddingBottom: 8,
            borderBottom: `2px solid ${group.color}33`,
          }}>
            <span style={{ fontSize: 20 }}>{group.icon}</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#333" }}>{group.series}</span>
            <span style={{
              fontSize: 11, color: group.color, fontWeight: 600,
              background: `${group.color}15`, borderRadius: 20, padding: "1px 8px",
              border: `1px solid ${group.color}33`,
            }}>
              {group.items.length} video{group.items.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* tutorial cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {group.items.map(item => {
              const isHighlighted = item.id === highlightId;
              return (
                <div
                  key={item.id}
                  ref={isHighlighted ? highlightRef : null}
                  style={{
                    display: "flex", alignItems: "center", gap: 16,
                    background: isHighlighted ? `${group.color}0e` : "#fff",
                    border: `1px solid ${isHighlighted ? group.color : "#eee"}`,
                    borderLeft: `4px solid ${isHighlighted ? group.color : "#eee"}`,
                    borderRadius: 10,
                    padding: "12px 16px",
                    transition: "border-color 0.2s, background 0.2s",
                    boxShadow: isHighlighted ? `0 2px 12px ${group.color}22` : "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                  onMouseEnter={e => { if (!isHighlighted) { e.currentTarget.style.borderColor = `${group.color}55`; e.currentTarget.style.background = "#fafafa"; }}}
                  onMouseLeave={e => { if (!isHighlighted) { e.currentTarget.style.borderColor = "#eee"; e.currentTarget.style.background = "#fff"; }}}
                >
                  {/* thumbnail */}
                  <div style={{
                    width: 60, height: 44, borderRadius: 8,
                    background: `linear-gradient(135deg, ${group.color}22 0%, ${group.color}44 100%)`,
                    border: `1px solid ${group.color}33`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, fontSize: 22,
                    position: "relative", overflow: "hidden",
                  }}>
                    {group.icon}
                    <span style={{
                      position: "absolute", bottom: 2, right: 3,
                      fontSize: 9, fontWeight: 700, color: group.color,
                      background: "#fff", borderRadius: 3, padding: "0 3px",
                    }}>
                      {item.id}
                    </span>
                  </div>

                  {/* text */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#222" }}>
                        {item.title}
                      </span>
                      {isHighlighted && (
                        <span style={{
                          fontSize: 9, background: group.color, color: "#fff",
                          borderRadius: 10, padding: "1px 6px", fontWeight: 700,
                        }}>
                          SUGGESTED
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: 12, color: "#666", lineHeight: 1.45,
                      overflow: "hidden", textOverflow: "ellipsis",
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                    }}>
                      {item.desc}
                    </div>
                  </div>

                  {/* badges + action */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      <span style={{
                        fontSize: 10, background: `${group.color}15`, color: group.color,
                        border: `1px solid ${group.color}33`, borderRadius: 20, padding: "1px 7px", fontWeight: 600,
                      }}>
                        {item.duration}
                      </span>
                      {item.priority === "P0" && (
                        <span style={{
                          fontSize: 10, background: "#fff3cd", color: "#856404",
                          border: "1px solid #ffc107", borderRadius: 20, padding: "1px 7px", fontWeight: 600,
                        }}>
                          Essential
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => onPlay({ ...item, series: group.series })}
                      style={{
                        background: group.color, color: "#fff",
                        border: "none", borderRadius: 6,
                        fontSize: 11, fontWeight: 600,
                        padding: "5px 14px", cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                      onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                    >
                      ▶ Watch
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
